import React, { useState, useEffect, useRef } from "react";
import { LandingPageRow } from "../LandingPageSection";
import styled from "@emotion/styled";
import { CustomThemeType } from "../../../../context/Providers";
import SectionFooter from "../SectionFooter";
import { ChevronLeft, ChevronRight, CircuitBoard, LayoutDashboard, ScanBarcode } from "lucide-react";
import { useWindowSize } from "react-use";
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "../../../../styles/swiper-overrides.css";

const CarouselSectionRow = styled(LandingPageRow)`
    @media (max-width: 900px) {
        flex-direction: column;
        height: auto !important;
        align-items: stretch;
    }
`;

const CarouselWrapper = styled.div<{ theme?: CustomThemeType }>`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
`;

const CarouselViewportShell = styled.div<{ theme?: CustomThemeType }>`
    width: 100%;
    padding: 0 clamp(1rem, 2.5vw, 3.25rem);
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
    background: ${p => p.theme.background};
    @media (max-width: 900px) {
        padding: 0;
    }
    &::before,
    &::after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        width: clamp(32px, 3vw, 64px);
        pointer-events: none;
        z-index: 5;
    }
    &::before {
        left: 0;
        background: linear-gradient(
            to right,
            ${p => p.theme.background},
            color-mix(in srgb, ${p => p.theme.background}, transparent 35%),
            transparent
        );
    }
    &::after {
        right: 0;
        background: linear-gradient(
            to left,
            ${p => p.theme.background},
            color-mix(in srgb, ${p => p.theme.background}, transparent 35%),
            transparent
        );
    }
`;

const CarouselViewport = styled(Swiper)<{ theme?: CustomThemeType }>`
    width: 100%;
    padding-bottom: 0.5rem;
    background: ${p => p.theme.background} !important;
    overflow: visible !important;
    .swiper-wrapper {
        align-items: stretch;
    }
    .swiper-slide {
        display: flex;
    }
`;

const SlideImageFrame = styled.div<{ theme?: CustomThemeType }>`
    width: 100%;
    position: relative;
    border-radius: 24px;
    overflow: hidden;
    background: color-mix(in srgb, ${p => p.theme.background || "#f6eee2"}, transparent 15%);
    filter: drop-shadow(0 35px 80px rgba(0, 0, 0, 0.4));
    &::before {
        content: "";
        display: block;
        padding-bottom: calc((2688 / 1242) * 100%);
    }
`;

const SlideImage = styled.img<{ theme?: CustomThemeType }>`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    cursor: zoom-in;
    transition: transform 250ms ease, box-shadow 250ms ease;
    object-fit: cover;
    background: ${p => p.theme.background};
    &:hover {
        transform: scale(1.02);
        box-shadow: 0 40px 90px rgba(0, 0, 0, 0.5);
    }
`;

const CarouselControls = styled.div<{ theme?: CustomThemeType }>`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const ControlButton = styled.button<{ theme?: CustomThemeType }>`
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    border: 1px solid color-mix(in srgb, ${p => p.theme.brand}, ${p => p.theme.background} 60%);
    background: ${p => p.theme.background};
    color: ${p => p.theme.brand};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 200ms ease, color 200ms ease, transform 200ms ease;
    &:hover {
        background: ${p => p.theme.brand};
        color: ${p => p.theme.background};
        transform: translateY(-2px);
    }
`;

const Col2 = styled.div<{ theme?: CustomThemeType }>`
    width: 40%;
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    z-index: 10;
    h1 {
        color: ${p => p.theme.foreground};
        padding: 0;
        margin: 0;
        font-size: 6rem;
        font-weight: 500;
    }
    h4 {
        color: color-mix(in hsl, ${p => p.theme.foreground}, ${p => p.theme.background} 50%);
        margin-bottom: 1.5rem;
        margin-top: 1rem;
        font-size: 3rem;
    }
    @media (max-width: 1500px) {
        width: 50%;
    }
    @media (max-width: 900px) {
        width: 100%;
        min-height: auto;
        align-items: flex-start;
        text-align: left;
        padding: 2rem 1.5rem 1rem;
    }
`;

const Col1 = styled.div<{ theme?: CustomThemeType }>`
    width: 45%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 6rem 0 4rem;
    @media (max-width: 1500px) {
        width: 50%;
        padding: 0 3rem;
    }
    @media (max-width: 900px) {
        width: 100%;
        padding: 2.5rem 1.75rem;
    }
`;

const BulletPoint = styled.div<{ theme?: CustomThemeType }>`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    margin: 1.5rem 0;
    width: 100%;
    gap: 2rem;
    & svg {
        color: ${p => p.theme.brand};
        width: 4.5rem;
        height: 4.5rem;
        flex-shrink: 0;
    }
    & h4 {
        color: color-mix(in srgb, ${p => p.theme.foreground}, ${p => p.theme.background} 10%);
        padding: 0;
        margin: 0;
        font-size: 2rem;
        line-height: 1.5;
        text-align: left;
    }
    @media (max-width: 900px) {
        justify-content: center;
        text-align: center;
        & h4 {
            text-align: center;
            font-size: 1.4rem;
        }
        & svg {
            width: 3.5rem;
            height: 3.5rem;
        }
    }
`;

const Section2Content: React.FC = () => {
    const { width } = useWindowSize();

    const carouselImages = [
        ...Array.from({ length: 9 }, (_, idx) =>
            `./assets/carosel/${String(idx + 1).padStart(2, "0")}.png`
        ),
        "./assets/carosel/10-v2.png",
    ];
    const slidesPerView = Math.min(width < 360 ? 1 : 2, carouselImages.length);
    const spaceBetween = width < 900 ? 16 : 28;
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const swiperRef = useRef<SwiperInstance | null>(null);

    useEffect(() => {
        if (expandedIndex !== null) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [expandedIndex]);

    useEffect(() => {
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key === "Escape") setExpandedIndex(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const goPrev = () => swiperRef.current?.slidePrev();
    const goNext = () => swiperRef.current?.slideNext();

    return (
        <>
            <CarouselSectionRow height={width > 650 ? "85vh" : "auto"}>
                <Col1>
                    <CarouselWrapper>
                        <CarouselViewportShell>
                            <CarouselViewport
                                modules={[Autoplay]}
                                slidesPerView={slidesPerView}
                                spaceBetween={spaceBetween}
                                autoplay={{ delay: 9000, disableOnInteraction: false }}
                                loop
                                onSwiper={(swiper) => {
                                    swiperRef.current = swiper;
                                }}
                            >
                                {carouselImages.map((src, index) => (
                                    <SwiperSlide key={src}>
                                        <SlideImageFrame>
                                            <SlideImage
                                                src={src}
                                                alt={`carousel-${index + 1}`}
                                                loading="lazy"
                                                onClick={() => setExpandedIndex(index)}
                                            />
                                        </SlideImageFrame>
                                    </SwiperSlide>
                                ))}
                            </CarouselViewport>
                        </CarouselViewportShell>
                        <CarouselControls>
                            <ControlButton type="button" onClick={goPrev} aria-label="Previous images">
                                <ChevronLeft />
                            </ControlButton>
                            <ControlButton type="button" onClick={goNext} aria-label="Next images">
                                <ChevronRight />
                            </ControlButton>
                        </CarouselControls>
                    </CarouselWrapper>
                </Col1>
                <Col2>
                    <BulletPoint>
                        <ScanBarcode size="5rem" />
                        <h4>Rest easy knowning alerts stop errors before they leave the truck.</h4>
                    </BulletPoint>
                    <BulletPoint>
                        <LayoutDashboard size="5rem" />
                        <h4>No dedicated device required—cloud scanning works from any mobile phone.</h4>
                    </BulletPoint>
                    <BulletPoint>
                        <CircuitBoard size="5rem" />
                        <h4>We handle the integrations so your team can breathe easier.</h4>
                    </BulletPoint>
                </Col2>
            </CarouselSectionRow>

            <Modal
                open={expandedIndex !== null}
                onClose={() => setExpandedIndex(null)}
                closeAfterTransition
            >
                <Fade in={expandedIndex !== null} timeout={300}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            justifyContent: { xs: 'center', md: 'flex-start' },
                            outline: 'none',
                            height: '100vh',
                            width: '100vw',
                            p: { xs: 2, md: 6 },
                            boxSizing: 'border-box',
                            overflowY: 'auto',
                        }}
                        onClick={() => setExpandedIndex(null)}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 2, md: 4 },
                                alignItems: 'stretch',
                                width: '100%',
                                maxWidth: '1100px',
                            }}
                        >
                            <Box
                                component="img"
                                src={expandedIndex !== null ? carouselImages[expandedIndex] : undefined}
                                alt={expandedIndex !== null ? `expanded-${expandedIndex}` : ''}
                                sx={{
                                    flex: { xs: '0 0 auto', md: '0 0 65%' },
                                    width: { xs: '100%', md: '65%' },
                                    borderRadius: '32px',
                                    maxHeight: '80vh',
                                    objectFit: 'cover',
                                    boxShadow: '0 35px 70px rgba(0,0,0,0.4)',
                                }}
                            />
                            <Box
                                sx={{
                                    flex: 1,
                                    backgroundColor: 'rgba(0,0,0,0.65)',
                                    color: '#fff',
                                    borderRadius: '28px',
                                    padding: { xs: 2.5, md: 4 },
                                    boxShadow: '0 35px 70px rgba(0,0,0,0.35)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                                    textAlign: 'center',
                                }}
                            >
                                Each mistake can cost thousands to fix.
                            </Box>
                        </Box>
                    </Box>
                </Fade>
            </Modal>

            <SectionFooter sectionIndex={2} />
        </>
    );
}

export default Section2Content;
